import React, {useEffect, useState} from 'react'
import App from './App'
import Keycloak from 'keycloak-js'
import {Button, Result, Spin} from 'antd'
import {observer} from 'mobx-react'
import {useStores} from '../../store'
import './styles/ant_overrides.css'
import {i18n} from '../../i18n'
import {ErrorBoundary} from '../common'

/**
 * The entry point of the application. Here will be checked the user permissions.
 * It is used as an App wrapper component.
 */
const EntryPoint: React.FC = observer(() => {
    const [failedInitKeycloak, setFailedInitKeycloak] = useState(false)

    const {userStore} = useStores()

    useEffect(() => {
        const keycloak = new Keycloak('/keycloak');
    
        // If you're using Create React App, use: process.env.NODE_ENV === 'development'
        const isDev = process.env.NODE_ENV === 'development' // Vite way to check dev mode
    
        const initOptions = {
            onLoad: 'login-required',
            checkLoginIframe: false,
        } as any;
    
        if (isDev) {
            // In DEV, try to restore tokens
            const existingToken = localStorage.getItem('keycloakToken');
            const existingRefreshToken = localStorage.getItem('keycloakRefreshToken');
            if (existingToken && existingRefreshToken) {
                initOptions.token = existingToken;
                initOptions.refreshToken = existingRefreshToken;
            }
        }
    
        keycloak
            .init(initOptions)
            .then((authenticated) => {
                if (authenticated) {
                    userStore.setKeycloak(keycloak);
    
                    if (isDev) {
                        // Save tokens only in DEV
                        localStorage.setItem('keycloakToken', keycloak.token || '');
                        localStorage.setItem('keycloakRefreshToken', keycloak.refreshToken || '');
                    }
                }
            })
            .catch(e => {
                console.error(e);
                setFailedInitKeycloak(true);
            });
    
        if (isDev) {
            // Only start auto-refresh in DEV
            const refreshInterval = setInterval(() => {
                keycloak.updateToken(70)
                    .then((refreshed) => {
                        if (refreshed) {
                            console.log('Token refreshed');
                            localStorage.setItem('keycloakToken', keycloak.token || '');
                            localStorage.setItem('keycloakRefreshToken', keycloak.refreshToken || '');
                        }
                    })
                    .catch(() => {
                        console.warn('Failed to refresh token, or session expired');
                        keycloak.logout();
                    });
            }, 60000);
    
            return () => clearInterval(refreshInterval);
        }
    }, []);
    
    

    if (failedInitKeycloak || userStore.userConfigs === undefined) {
        return (
            <Result
                status='500'
                title={i18n.t('common.errors.general')}
                subTitle={failedInitKeycloak ? i18n.t('common.errors.keycloak') : i18n.t('common.errors.configs')}
                extra={
                    <Button type='primary' onClick={() => window.location.reload()}>
                        {i18n.t('button.refresh')}
                    </Button>
                }
            />
        )
    }

    if (!userStore.keycloak || userStore.userConfigs === null) {
        return (
            <div style={{display: 'flex', justifyContent: 'center', marginTop: '25%'}}>
                <Spin size={'large'} />
            </div>
        )
    }

    return (
        <ErrorBoundary>
            <App />
        </ErrorBoundary>
    )
})

export default EntryPoint
